/**
 * Plays a tone from the shared catalogue, with Web Audio oscillators.
 *
 * ## The autoplay rule, stated plainly
 *
 * Every browser refuses to make noise until the page has had a real user gesture. That
 * is not a bug to work around; it is the rule, and it cannot be defeated. What it means
 * here is that a freshly loaded, never-touched tab — exactly the state a wallboard sits
 * in for eight hours — is SILENT, and would stay silent through a tornado warning while
 * appearing to be configured for sound.
 *
 * So this module never pretends. `audioState()` reports whether audio is genuinely
 * available, `unlockAudio()` is called from a click handler, and the UI shows an
 * explicit "Enable sound" control until it succeeds. A silent dashboard that says it is
 * silent is safe; one that believes it is armed is not.
 */

import { toneById, TONE_SILENT, type ToneDef } from '@ww/shared';

export type AudioState = 'ready' | 'blocked' | 'unsupported';

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;
let unsupported = false;

function context(): AudioContext | null {
  if (unsupported) return null;
  if (ctx) return ctx;
  const Ctx: Ctor | undefined =
    typeof window === 'undefined'
      ? undefined
      : window.AudioContext ??
        (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!Ctx) {
    unsupported = true;
    return null;
  }
  try {
    ctx = new Ctx();
  } catch {
    unsupported = true;
    return null;
  }
  return ctx;
}

export function audioState(): AudioState {
  const c = context();
  if (!c) return 'unsupported';
  return c.state === 'running' ? 'ready' : 'blocked';
}

/**
 * Ask the browser for permission to make noise. MUST be called from a user gesture (a
 * click or a key press) or it resolves to 'blocked', costing nothing but a promise.
 */
export async function unlockAudio(): Promise<AudioState> {
  const c = context();
  if (!c) return 'unsupported';
  try {
    await c.resume();
  } catch {
    /* still blocked; the caller shows the enable control again */
  }
  return c.state === 'running' ? 'ready' : 'blocked';
}

/** Longest a single play may occupy the scheduler. */
const MAX_TONE_MS = 6_000;

/**
 * Play one tone.
 *
 * Returns the duration actually scheduled in ms, or 0 if nothing sounded — so a caller
 * can space repeated alerts without guessing, and a preview button can re-enable itself
 * at the right moment.
 */
export function playTone(toneId: string, volume = 0.7): number {
  if (!toneId || toneId === TONE_SILENT) return 0;
  const tone = toneById(toneId);
  if (!tone) return 0;
  const c = context();
  if (!c || c.state !== 'running') return 0;

  const vol = Math.min(1, Math.max(0, volume)) * tone.gain;
  if (vol <= 0) return 0;

  let at = c.currentTime + 0.02;
  const start = at;

  for (let rep = 0; rep < Math.max(1, tone.repeat); rep += 1) {
    for (const step of tone.steps) {
      const dur = step.ms / 1000;
      if ((at - start) * 1000 > MAX_TONE_MS) break;
      playStep(c, tone, step.freq, at, dur, vol);
      // A second oscillator at the same instant is what makes a two-tone signal read as
      // one sound rather than as two beeps. Halved, because two tones sum in amplitude.
      if (step.freq2) playStep(c, tone, step.freq2, at, dur, vol * 0.5);
      at += dur + (step.gapMs ?? 0) / 1000;
    }
  }
  return Math.round((at - start) * 1000);
}

/**
 * One note, with a short fade in and out.
 *
 * The envelope is not decoration. A square wave switched on and off at full amplitude
 * produces a click on every edge — through a cheap desk speaker at volume that click is
 * louder than the tone, and a four-beep alarm arrives as eight cracks.
 */
function playStep(
  c: AudioContext,
  tone: ToneDef,
  freq: number,
  at: number,
  dur: number,
  vol: number,
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = tone.wave;
  osc.frequency.setValueAtTime(freq, at);

  const edge = Math.min(0.012, dur / 4);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(vol, at + edge);
  gain.gain.setValueAtTime(vol, at + dur - edge);
  gain.gain.linearRampToValueAtTime(0, at + dur);

  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + dur + 0.01);
  // Nodes are single-use; without this the graph grows for as long as the tab is open.
  osc.onended = () => {
    try {
      osc.disconnect();
      gain.disconnect();
    } catch {
      /* already torn down */
    }
  };
}
